import { useState } from 'react';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    description: '',
    website_url: '' // Honeypot field
  });

  const [status, setStatus] = useState('idle'); // 'idle' | 'submitting' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage('');

    // Basic validation
    if (!formData.name || !formData.phone || !formData.email || !formData.description) {
      setStatus('error');
      setErrorMessage('Udfyld venligst alle påkrævede felter.');
      return;
    }

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          description: formData.description,
          website_url: formData.website_url // Mandatory honeypot
        })
      });

      if (response.ok) {
        setStatus('success');
        setFormData({
          name: '',
          phone: '',
          email: '',
          description: '',
          website_url: ''
        });
      } else {
        const errorData = await response.json().catch(() => ({}));
        setStatus('error');
        setErrorMessage(errorData.message || 'Der opstod en fejl under afsendelsen. Prøv venligst igen.');
      }
    } catch (err) {
      setStatus('error');
      setErrorMessage('Netværksfejl. Kontroller din internetforbindelse og prøv igen.');
    }
  };

  return (
    <div className="bg-white p-8 md:p-10 rounded-xl border border-stone-200 shadow-sm">
      <h3 className="font-display text-2xl font-bold text-stone-900 mb-2">Udfyld kontaktformularen her</h3>
      <p className="text-sm text-stone-500 mb-8">Vi vender tilbage til dig hurtigst muligt for en uforpligtende snak om dit projekt.</p>

      {status === 'success' ? (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 p-6 rounded-lg flex flex-col items-center text-center gap-3 animate-fade-in">
          <i className="fa-solid fa-circle-check text-4xl text-emerald-500" />
          <h4 className="font-display text-lg font-semibold">Tak for din henvendelse!</h4>
          <p className="text-sm text-emerald-700">Vi har modtaget din besked og kontakter dig hurtigst muligt.</p>
          <button 
            onClick={() => setStatus('idle')}
            className="mt-2 text-xs font-semibold text-emerald-800 underline hover:text-emerald-900"
          >
            Send en ny besked
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="flex flex-col gap-6">
          {/* Honeypot field - ABSOLUTELY HIDDEN from humans */}
          <div className="sr-only absolute -top-[9999px] -left-[9999px]" aria-hidden="true">
            <label htmlFor="website_url">Do not fill this field</label>
            <input
              id="website_url"
              type="text"
              name="website_url"
              value={formData.website_url}
              onChange={handleChange}
              tabIndex="-1"
              autoComplete="off"
            />
          </div>

          {/* Name */}
          <div className="flex flex-col gap-2">
            <label htmlFor="name" className="text-sm font-semibold text-stone-700">
              Navn <span className="text-accent">*</span>
            </label>
            <input
              id="name"
              type="text"
              name="name"
              required
              value={formData.name}
              onChange={handleChange}
              placeholder="Dit fulde navn"
              className="w-full px-4 py-3 rounded-md border border-stone-300 focus:border-accent focus:ring-1 focus:ring-accent outline-none transition-all text-sm"
              disabled={status === 'submitting'}
            />
          </div>

          {/* Phone & Email Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Phone */}
            <div className="flex flex-col gap-2">
              <label htmlFor="phone" className="text-sm font-semibold text-stone-700">
                Telefonnummer <span className="text-accent">*</span>
              </label>
              <input
                id="phone"
                type="tel"
                name="phone"
                required
                value={formData.phone}
                onChange={handleChange}
                placeholder="Dit telefonnummer"
                className="w-full px-4 py-3 rounded-md border border-stone-300 focus:border-accent focus:ring-1 focus:ring-accent outline-none transition-all text-sm"
                disabled={status === 'submitting'}
              />
            </div>

            {/* Email */}
            <div className="flex flex-col gap-2">
              <label htmlFor="email" className="text-sm font-semibold text-stone-700">
                E-mail <span className="text-accent">*</span>
              </label>
              <input
                id="email"
                type="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                placeholder="din.email@adresse.dk"
                className="w-full px-4 py-3 rounded-md border border-stone-300 focus:border-accent focus:ring-1 focus:ring-accent outline-none transition-all text-sm"
                disabled={status === 'submitting'}
              />
            </div>
          </div>

          {/* Description */}
          <div className="flex flex-col gap-2">
            <label htmlFor="description" className="text-sm font-semibold text-stone-700">
              Beskrivelse af dit projekt <span className="text-accent">*</span>
            </label>
            <textarea
              id="description"
              name="description"
              required
              rows="5"
              value={formData.description}
              onChange={handleChange}
              placeholder="Fortæl os kort om dit projekt, dine ønsker og tidsramme..."
              className="w-full px-4 py-3 rounded-md border border-stone-300 focus:border-accent focus:ring-1 focus:ring-accent outline-none transition-all text-sm resize-none"
              disabled={status === 'submitting'}
            />
          </div>

          {/* Error Message */}
          {status === 'error' && (
            <div className="bg-rose-50 border border-rose-200 text-rose-800 p-4 rounded-md text-sm flex items-center gap-2">
              <i className="fa-solid fa-circle-exclamation text-rose-500" />
              <span>{errorMessage}</span>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={status === 'submitting'}
            className="bg-primary hover:bg-primary-light text-white font-medium py-4 px-6 rounded-md transition-all duration-300 flex items-center justify-center gap-2 hover:shadow-lg cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {status === 'submitting' ? (
              <>
                <i className="fa-solid fa-spinner animate-spin" />
                <span>Sender besked...</span>
              </>
            ) : (
              <>
                <span>Send besked</span>
                <i className="fa-solid fa-paper-plane text-xs" />
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
}
